/**
 * OtpCodeInput — single-field 6-digit TOTP input (HoD §4.2).
 *
 * Industry-standard one-field TOTP entry — Stripe / Linear / Vercel /
 * GitHub / Google all use a single field. Segmented inputs are
 * accessibility-fragile (screen readers stutter, paste behavior is
 * inconsistent, password-manager autofill misfires). The single field
 * with ``inputmode="numeric"``, ``autoComplete="one-time-code"``,
 * monospace + wide tracking gives the segmented look without the
 * segmented complexity.
 *
 * Behavior:
 *   - Strips non-digit input on every keystroke.
 *   - Autosubmits when value reaches 6 digits (fires ``onAutoSubmit``).
 *   - Clears + refocuses on error (parent resets ``value`` after a
 *     failed submission).
 */
import { type ChangeEvent, forwardRef, useEffect, useRef } from "react";
import { Input } from "@/components/common/Input";

interface OtpCodeInputProps {
  value: string;
  onChange: (v: string) => void;
  /** Fired when ``value`` reaches 6 digits via user input. */
  onAutoSubmit?: () => void;
  hasError?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  /** Defaults to "Six-digit verification code". */
  ariaLabel?: string;
  id?: string;
}

export const OtpCodeInput = forwardRef<HTMLInputElement, OtpCodeInputProps>(
  function OtpCodeInput(
    {
      value,
      onChange,
      onAutoSubmit,
      hasError = false,
      autoFocus = false,
      disabled = false,
      ariaLabel = "Six-digit verification code",
      id,
    },
    ref,
  ): JSX.Element {
    const internalRef = useRef<HTMLInputElement | null>(null);
    const setRef = (el: HTMLInputElement | null) => {
      internalRef.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) ref.current = el;
    };

    // Autosubmit on 6 digits — fires exactly once per "transition into
    // 6 chars." Guarded by a ref so a parent re-render that re-creates
    // ``onAutoSubmit`` does not re-fire the submit on a value that's
    // been at 6 the whole time (which caused an infinite render loop in
    // unit tests). Reset when ``value`` drops back below 6 so a wrong-
    // code clear-and-retry pass triggers the next submit cleanly.
    const lastSubmittedValueRef = useRef<string | null>(null);
    useEffect(() => {
      if (value.length < 6) {
        lastSubmittedValueRef.current = null;
        return;
      }
      if (
        !disabled
        && onAutoSubmit
        && lastSubmittedValueRef.current !== value
      ) {
        lastSubmittedValueRef.current = value;
        onAutoSubmit();
      }
    }, [value, disabled, onAutoSubmit]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
      const stripped = e.target.value.replace(/\D/g, "").slice(0, 6);
      onChange(stripped);
    };

    return (
      <Input
        ref={setRef}
        id={id}
        type="text"
        inputMode="numeric"
        pattern="\d{6}"
        maxLength={6}
        autoComplete="one-time-code"
        autoFocus={autoFocus}
        disabled={disabled}
        hasError={hasError}
        value={value}
        onChange={handleChange}
        aria-label={ariaLabel}
        className="text-center font-mono text-lg uppercase tracking-[0.4em]"
      />
    );
  },
);
